import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Loader2 } from "lucide-react";
import { API, axios } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    axios.get(`${API}/posts/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: data?.post ? `${data.post.title} | BrandToBuilt` : "Blog | BrandToBuilt",
    description: data?.post ? (data.post.meta_description || data.post.excerpt || "") : undefined,
    path: `/blog/${slug}`,
  });

  if (notFound) {
    return (
      <div className="pt-40 pb-24 min-h-screen max-w-3xl mx-auto px-5 text-center" data-testid="blog-notfound">
        <h1 className="font-display font-black text-4xl mb-4">Article not found</h1>
        <button onClick={() => navigate("/blog")} className="text-[#ccff00]">← Back to the blog</button>
      </div>
    );
  }

  if (!data) {
    return <div className="pt-40 min-h-screen flex justify-center text-zinc-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>;
  }

  const { post, related } = data;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt || "",
    image: post.cover_image,
    datePublished: post.published_at,
    author: { "@type": "Organization", name: "BrandToBuilt" },
    publisher: { "@type": "Organization", name: "BrandToBuilt" },
  };

  return (
    <article className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="blog-post">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#ccff00] transition-colors mb-8" data-testid="post-back">
          <ArrowLeft size={16} /> All articles
        </Link>

        <div className="flex items-center gap-3 text-xs text-zinc-500 mb-5">
          <span className="text-[#ccff00] uppercase tracking-widest font-semibold">{post.category}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {post.read_time} min read</span>
        </div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-tight mb-8" data-testid="post-title">
          {post.title}
        </motion.h1>

        <div className="rounded-2xl overflow-hidden border border-white/10 mb-10">
          <img src={post.cover_image} alt={post.title} className="w-full h-64 md:h-96 object-cover" />
        </div>

        <div className="blog-content" data-testid="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />

        {post.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-white/10">
            {post.keywords.map((k, i) => (
              <span key={i} className="text-xs rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-zinc-400">{k}</span>
            ))}
          </div>
        )}

        <div className="mt-14 rounded-2xl bg-[#ccff00] text-black p-8 md:p-10 text-center">
          <h3 className="font-display font-black text-2xl md:text-3xl mb-3">Ready to get your business online?</h3>
          <p className="text-black/70 mb-6">Brand, website and full setup — live in 48 hours. From £199.</p>
          <button onClick={() => { navigate("/"); setTimeout(() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }), 400); }} className="rounded-full bg-black text-[#ccff00] font-bold uppercase tracking-widest text-xs px-8 py-4 hover:scale-105 transition-transform" data-testid="post-cta">
            Start my 48-hour launch
          </button>
        </div>
      </div>

      {related?.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
          <h3 className="font-display font-bold text-2xl mb-8">Keep reading</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group block rounded-2xl overflow-hidden bg-[#121212] border border-white/10 hover:border-[#ccff00]/40 transition-colors" data-testid={`related-${p.slug}`}>
                <div className="h-40 overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <span className="text-[#ccff00] text-xs uppercase tracking-widest">{p.category}</span>
                  <h4 className="font-display font-bold text-base mt-2 group-hover:text-[#ccff00] transition-colors flex items-center gap-1">{p.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default BlogPost;
